import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import { useSearchParams } from 'react-router-dom';
import { colors } from '@budgit/design-tokens';
import { useAuth } from '../contexts/AuthContext';
import {
  beforeStoreRedirect,
  buildDownloadDestinations,
  claimAutoStoreRedirect,
  detectMobileStorePlatform,
  openStoreUrl,
  readAppStoreProviderToken,
} from '../services/storeNavigation';
import { subscribeAnalyticsConsent } from '../services/analyticsConsent';
import { trackXDownloadPageView } from '../services/xAdsPixel';

const Download = () => {
  const [showOptions, setShowOptions] = useState(false);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const providerToken = readAppStoreProviderToken();
  const search = `?${searchParams.toString()}`;

  const destinations = useMemo(
    () => buildDownloadDestinations(search, { providerToken }),
    [search, providerToken]
  );

  // #1221: post-web-signup / incomplete-onboarding handoff lands here.
  const isWebSignupHandoff = useMemo(() => {
    const source = (searchParams.get('source') || '').toLowerCase();
    return source === 'web-signup' || source === 'web_signup' || source === 'handoff';
  }, [searchParams]);

  useEffect(() => {
    const platform = detectMobileStorePlatform({
      userAgent: navigator.userAgent,
      vendor: navigator.vendor,
      opera: window.opera,
      msStream: window.MSStream,
    });
    if (!platform) {
      setShowOptions(true);
      return;
    }
    // StrictMode remounts effects in dev. Claim once so the event fires once.
    if (!claimAutoStoreRedirect()) return undefined;
    beforeStoreRedirect(platform, {
      surface: 'redirect',
      search,
      providerToken,
    }).then((url) => {
      openStoreUrl(url, { replace: true });
    }).catch(() => {
      setShowOptions(true);
    });
    return undefined;
  }, [search, providerToken]);

  const openStore = (platform) => (event) => {
    event.preventDefault();
    beforeStoreRedirect(platform, {
      surface: 'button',
      search,
      providerToken,
    }).then((url) => {
      openStoreUrl(url, { replace: false });
    }).catch(() => {});
  };

  useEffect(() => {
    trackXDownloadPageView();
    return subscribeAnalyticsConsent(() => {
      trackXDownloadPageView();
    });
  }, []);

  if (!showOptions) {
    // Mobile users see nothing while redirecting (happens immediately).
    return null;
  }

  const headline = isWebSignupHandoff
    ? "You're signed up — finish setup in the app"
    : 'Download Budgit';

  const body = isWebSignupHandoff
    ? 'Budgit onboarding (connect accounts, budget, and more) runs in the mobile app. Download Budgit, sign in with the same email or phone, and continue where you left off.'
    : 'Get the app on your mobile device';

  const signedInAs =
    isWebSignupHandoff && (user?.email || user?.phone)
      ? user.email || user.phone
      : null;

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: colors.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center' }}>
          <img
            src="/images/budgit-logo-md.png"
            alt="Budgit Logo"
            style={{ height: 80, marginBottom: 24 }}
          />
          <Typography variant="h4" sx={{
            fontWeight: 700,
            color: colors.primary,
            mb: 2
          }}>
            {headline}
          </Typography>
          <Typography variant="body1" sx={{
            color: colors.secondary,
            mb: signedInAs ? 1 : 4
          }}>
            {body}
          </Typography>
          {signedInAs ? (
            <Typography variant="body2" sx={{ color: colors.secondary, mb: 4, opacity: 0.85 }}>
              Signed in as <strong>{signedInAs}</strong>
            </Typography>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              href={destinations.ios}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openStore('ios')}
              startIcon={<AppleIcon />}
              sx={{
                backgroundColor: colors.secondary,
                color: colors.white,
                py: 1.5,
                '&:hover': {
                  backgroundColor: colors.secondary,
                  opacity: 0.9,
                },
              }}
            >
              Download on the App Store
            </Button>

            <Button
              variant="contained"
              size="large"
              href={destinations.android}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openStore('android')}
              startIcon={
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
              }
              sx={{
                backgroundColor: colors.primary,
                color: colors.white,
                py: 1.5,
                '&:hover': {
                  backgroundColor: colors.primary,
                  opacity: 0.9,
                },
              }}
            >
              Get it on Google Play
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Download;
